import type { TFunction } from 'i18next';
import type { IFolder } from '@apis/documents';
import { folderDisplayName } from './folderDisplayName';
import { buildTree, type FolderNode } from './buildTree';
import type { IPickComboOption } from '@components/ui/PickCombo/PickCombo';

/**
 * The full "Root / Sub / Leaf /" path label for a folder id, built by walking
 * parentId up the flat folder list, each segment through the shared display
 * naming (roots get their localized label). Null when the folder isn't in the
 * list — the caller supplies its own fallback.
 */
export function folderPathLabel(t: TFunction, folders: IFolder[], folderId: string): string | null {
  const foldersById = new Map(folders.map((folder) => [folder.id, folder]));
  const current = foldersById.get(folderId);
  if (!current) return null;
  const segments: string[] = [];
  for (let node: IFolder | undefined = current; node; node = node.parentId ? foldersById.get(node.parentId) : undefined) {
    segments.unshift(folderDisplayName(t, node));
  }
  return `${segments.join(' / ')} /`;
}

/**
 * Every folder as a pick option labelled with its full path, in the sidebar's
 * order: each folder directly followed by its subfolders. A branch still saving
 * is left out — its ids are about to change.
 */
export function folderPathOptions(t: TFunction, folders: IFolder[]): IPickComboOption[] {
  const options: IPickComboOption[] = [];
  const visit = (node: FolderNode) => {
    if (node.saving) return;
    options.push({ value: node.id, label: folderPathLabel(t, folders, node.id)! });
    node.children.forEach(visit);
  };
  buildTree(folders, '').children.forEach(visit);
  return options;
}

/**
 * Where `id` (a folder or file id) lives after folder `oldId` was renamed to
 * `newId`. Ids are paths, so the renamed folder and everything under it change;
 * null for anything outside it.
 */
export function idAfterFolderRename(id: string, oldId: string, newId: string): string | null {
  if (id === oldId) return newId;
  return id.startsWith(`${oldId}/`) ? newId + id.slice(oldId.length) : null;
}
